import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Alert, MedicalAlert, PatientAlert, AlertActionButtonsComponent, AlertActionButtonsService } from '@libs/shared-services';
import { SvgIconComponent, SvgIcons } from '@ngneat/svg-icon';
import { CdkAccordionItem } from '@angular/cdk/accordion';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Dialog } from '@angular/cdk/dialog';
import { HealthHistoryDialogComponent, HealthHistoryDialogData } from '@libs/home';
import { selectUserSiteId, patientActions } from '@libs/store';
import { routes } from '@libs/utils';
import { Store } from '@ngrx/store';

@Component({
  selector: 'lib-schedule-alert',
  templateUrl: './schedule-alert.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, SvgIconComponent, AlertActionButtonsComponent, CdkAccordionItem],
  animations: [
    trigger('slideToggle', [
      state('collapsed', style({ height: '0px', opacity: 0, overflow: 'hidden' })),
      state('expanded', style({ height: '*', opacity: 1, overflow: 'hidden' })),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
    ]),
  ],
})
export class ScheduleAlertComponent implements OnDestroy, OnInit {
  @Input({ required: true }) public alert!: Alert;
  @Input() public isExpanded = false;
  @Output() public toggleExpanded = new EventEmitter<void>();
  @Output() public clearExpandedIndex = new EventEmitter<void>();

  private readonly store = inject(Store);
  private readonly dialog = inject(Dialog);

  private readonly alertActionButtonsService = inject(AlertActionButtonsService);
  public shouldShowAlert = true;

  public ngOnInit(): void {
    this.shouldShowAlert = this.alertActionButtonsService.shouldShowAlert(this.alert);
  }

  public ngOnDestroy(): void {
    this.clearExpandedIndex.emit();
  }

  public onToggleExpanded(): void {
    this.toggleExpanded.emit();
  }

  public get isMedicalAlert(): boolean {
    return this.alert.type === 'medical';
  }

  public get isPatientAlert(): boolean {
    return this.alert.type === 'patient';
  }

  public get medicalAlert(): MedicalAlert {
    return this.alert as MedicalAlert;
  }

  public get patientAlert(): PatientAlert {
    return this.alert as PatientAlert;
  }

  public get iconColor(): string {
    return this.isMedicalAlert ? 'text-states-deep-red' : 'text-states-orange';
  }

  public get iconKey(): SvgIcons {
    return this.isMedicalAlert ? 'px-16-medical-alert' : 'px-16-patient-alert';
  }

  public onHideAlert(): void {
    this.shouldShowAlert = false;
  }

  public onOpenHealthHistory(): void {
    const siteId = this.store.selectSignal(selectUserSiteId)() ?? routes.showcaseSiteId;

    this.store.dispatch(patientActions.onOpenHealthHistory({ patientId: this.alert.patientId }));
    this.dialog.open<HealthHistoryDialogComponent, HealthHistoryDialogData>(HealthHistoryDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[1356px]', 'h-full', 'pt-[84px]', 'pb-[12px]'],
      data: {
        siteId,
        patientId: this.alert.patientId,
        activeTabIndex: this.isMedicalAlert ? 0 : 2,
      },
    });
  }
}
